import React from "react";
import { BookOpen, ShoppingCart, Heart, School, Mail, Phone, Euro, Truck, Calendar } from "lucide-react";
import { motion } from "framer-motion";
import PageLayout from "@/components/PageLayout";
import Section from "@/components/Section";
import SectionHeader from "@/components/SectionHeader";
import { Button } from "@/components/Button";

// ============================================
// IMMAGINI
// ============================================

const BOOK_COVER = "/images/laquila-cecilia-copertina.jpg";

// ============================================
// COMPONENTE FEATURE CARD
// ============================================

interface FeatureCardProps {
  icon: React.ElementType;
  title: string;
  description: string;
  index: number;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ icon: Icon, title, description, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: index * 0.1 }}
    className="bg-white rounded-xl p-6 shadow-elegant border border-gray-100 text-center"
  >
    <div className="inline-flex items-center justify-center w-14 h-14 bg-primary/10 rounded-full mb-4">
      <Icon size={28} className="text-primary" />
    </div>
    <h3 className="text-lg font-heading font-bold text-foreground mb-2">{title}</h3>
    <p className="text-text-light text-sm">{description}</p>
  </motion.div>
);

// ============================================
// COMPONENTE INFO BOX
// ============================================

interface InfoBoxProps {
  icon: React.ElementType;
  label: string;
  value: string;
}

const InfoBox: React.FC<InfoBoxProps> = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3">
    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
      <Icon size={20} className="text-primary" />
    </div>
    <div>
      <p className="text-sm text-text-muted">{label}</p>
      <p className="font-semibold text-foreground">{value}</p>
    </div>
  </div>
);

// ============================================
// COMPONENTE PRINCIPALE
// ============================================

const LAquilaCecilia: React.FC = () => {
  return (
    <PageLayout>
      {/* Hero Section - Book Presentation */}
      <Section background="cream" className="pt-24 md:pt-28">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Book Cover */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="flex justify-center lg:justify-end"
            >
              <div className="relative">
                <img
                  src={BOOK_COVER}
                  alt="L'Aquila Cecilia - Copertina del libro"
                  className="w-72 md:w-80 rounded-lg shadow-elegant-lg"
                />
                {/* Badge */}
                <div className="absolute -top-3 -right-3 bg-secondary text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                  4-10 anni
                </div>
              </div>
            </motion.div>

            {/* Book Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <p className="text-primary font-medium mb-2">di Alessandro Serreli</p>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-primary mb-4">
                L'Aquila Cecilia
              </h1>
              <p className="text-xl md:text-2xl text-secondary font-heading mb-6">
                storia favolosa di una Città meravigliosa
              </p>

              <p className="text-text-light font-body leading-relaxed mb-6 text-lg">
                Una raccolta di <strong className="text-foreground">brevi racconti per bambini</strong> (4-10 anni), che
                illustra in modo semplice i momenti chiave della{" "}
                <strong className="text-foreground">storia della Città di L'Aquila</strong> attraverso il racconto di
                una piccola aquila di nome Cecilia.
              </p>

              <p className="text-primary font-medium mb-8">
                Un progetto che nasce dalla volontà di valorizzare il territorio e la sua storia, cercando di farlo
                conoscere a chi lo vivrà in futuro: <strong>i nostri figli!</strong>
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  href="https://forms.gle/pWdciBhPYBgkenF88"
                  variant="primary"
                  size="lg"
                  icon={<BookOpen size={20} />}
                >
                  Leggi Ora Gratis
                </Button>
                <Button
                  href="https://docs.google.com/forms/d/e/1FAIpQLSesPlkFP-I7FNfmSpfEG1HAv2aYLxVldFiZPMr-tzDEJE55Sw/viewform?usp=publish-editor"
                  variant="outline"
                  size="lg"
                  icon={<ShoppingCart size={20} />}
                >
                  Prenota Copia Cartacea
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </Section>

      {/* Leggi Gratuitamente */}
      <Section background="white">
        <div className="max-w-4xl mx-auto text-center">
          <SectionHeader title="Leggi Gratuitamente" subtitle="Il racconto è disponibile da smartphone, PC e tablet" />

          <p className="text-text-light font-body leading-relaxed mb-8 text-lg">
            Potrai scegliere se leggere il racconto <strong className="text-foreground">in anonimato</strong> oppure{" "}
            <strong className="text-foreground">inserendo i tuoi dati</strong>. Se scegli questa seconda opzione ti
            chiederemo solamente il tuo Nome e Cognome e, solo se vorrai, potrai lasciarci anche il tuo numero di
            telefono e/o la tua Email per ricevere informazioni sulle nostre iniziative.
          </p>

          <Button
            href="https://forms.gle/pWdciBhPYBgkenF88"
            variant="secondary"
            size="lg"
            icon={<BookOpen size={20} />}
          >
            Leggi Ora
          </Button>
        </div>
      </Section>

      {/* Prenota Copia Stampata */}
      <Section background="gray" id="prenota">
        <div className="max-w-5xl mx-auto">
          <SectionHeader
            title="Prenota la Tua Copia Stampata"
            subtitle="Porta Cecilia nella tua libreria o regalala a un piccolo esploratore"
          />

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-elegant"
            >
              <p className="text-text-light font-body leading-relaxed mb-6">
                Sappiamo quanto sia magico leggere insieme ai propri figli, seguire le immagini con le dita e sentire il
                profumo della carta: per questo abbiamo deciso di far partire il primo ordine di copie!
              </p>

              <div className="bg-primary/5 rounded-xl p-4 mb-6">
                <p className="text-primary font-bold text-center flex items-center justify-center gap-2">
                  <Calendar size={20} />
                  Dal 7 Gennaio andremo in stampa
                </p>
              </div>

              <p className="text-text-light font-body leading-relaxed mb-6">
                Stiamo raccogliendo le prenotazioni per chi desidera portare Cecilia nella propria libreria o regalarla
                a un piccolo esploratore. La raccolta è in <strong>formato A5</strong>.
              </p>

              {/* Info boxes */}
              <div className="space-y-4">
                <InfoBox icon={Euro} label="Il costo" value="13€ per copia" />
                <InfoBox icon={Truck} label="Il pagamento" value="Alla consegna delle stampe" />
                <InfoBox icon={ShoppingCart} label="La consegna" value="A mano o spedizione" />
              </div>

              <p className="text-sm text-text-muted mt-6">
                In caso di spedizione, oltre al costo della raccolta sarà da aggiungere quello della spedizione con
                Poste Italiane ed il pagamento avverrà con bonifico sul C/C dell'Associazione.
              </p>
            </motion.div>

            {/* CTA Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-primary to-primary-dark rounded-2xl p-8 text-white text-center"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-full mb-6">
                <ShoppingCart size={40} />
              </div>
              <h3 className="text-2xl font-heading font-bold mb-4">Prenota Ora</h3>
              <p className="text-white/80 mb-8">Compila il form per prenotare la tua copia di "L'Aquila Cecilia"</p>
              <Button
                href="https://docs.google.com/forms/d/e/1FAIpQLSesPlkFP-I7FNfmSpfEG1HAv2aYLxVldFiZPMr-tzDEJE55Sw/viewform?usp=publish-editor"
                variant="secondary"
                size="lg"
                className="w-full"
              >
                Prenota Ora
              </Button>
            </motion.div>
          </div>
        </div>
      </Section>

      {/* Sostieni il Progetto */}
      <Section background="white">
        <div className="max-w-4xl mx-auto">
          <SectionHeader title="Sostieni il Nostro Lavoro" subtitle="Ti aspettiamo a braccia aperte! Potrai farlo:" />

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <FeatureCard
              icon={Heart}
              title="Diventa Socio"
              description="Partecipando attivamente alla nostra vita associativa, come tesserato o come tesserato sostenitore"
              index={0}
            />
            <FeatureCard
              icon={BookOpen}
              title="Acquista il Libro"
              description="Acquistando una copia cartacea del racconto per te o per regalarlo"
              index={1}
            />
            <FeatureCard
              icon={ShoppingCart}
              title="Sponsorizza"
              description="Supportando o sponsorizzando la nostra associazione nel modo che ritieni più opportuno"
              index={2}
            />
          </div>

          <div className="text-center">
            <Button href="/tesseramento" variant="primary" size="lg">
              Info Tesseramento
            </Button>
          </div>
        </div>
      </Section>

      {/* Scuole e Associazioni */}
      <Section background="cream">
        <div className="max-w-4xl mx-auto">
          <SectionHeader
            title="Scuole e Associazioni No Profit"
            subtitle="Un'offerta speciale per chi educa le nuove generazioni"
          />

          <div className="bg-white rounded-2xl p-8 shadow-elegant">
            <p className="text-text-light font-body leading-relaxed mb-6 text-center text-lg">
              Se rappresenti una <strong className="text-foreground">scuola</strong> o un'{" "}
              <strong className="text-foreground">associazione no profit</strong>, contattaci!
            </p>

            <div className="bg-secondary/10 rounded-xl p-6 mb-6">
              <p className="text-center text-foreground font-medium mb-4">
                Invia un'email a{" "}
                <a href="mailto:99castelli@gmail.com" className="text-primary hover:underline font-bold">
                  99castelli@gmail.com
                </a>
              </p>
              <p className="text-center text-text-light text-sm">
                Scrivi il nome della scuola o dell'associazione e descrivi brevemente di cosa vi occupate.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-primary/5 rounded-xl p-5 text-center">
                <p className="text-primary font-bold mb-2">Prime 10 prenotazioni</p>
                <p className="text-text-light text-sm">
                  Alle prime 10 prenotazioni (in ordine di ricezione email) forniremo una <strong>copia omaggio</strong>{" "}
                  del racconto.
                </p>
              </div>
              <div className="bg-secondary/5 rounded-xl p-5 text-center">
                <p className="text-secondary font-bold mb-2">Altre scuole</p>
                <p className="text-text-light text-sm">
                  A tutti gli altri forniremo il <strong>PDF pronto</strong> per essere stampato in autonomia.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* CTA Finale */}
      <Section background="primary">
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">Buona Lettura!</h2>
          <p className="text-white/80 mb-8 text-lg">
            Scopri la storia di L'Aquila attraverso gli occhi di Cecilia, una piccola aquila coraggiosa che ti guiderà
            alla scoperta della città dei 99 castelli.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              href="https://forms.gle/pWdciBhPYBgkenF88"
              variant="secondary"
              size="lg"
              icon={<BookOpen size={20} />}
            >
              Leggi Ora Gratis
            </Button>
            <Button href="/castelli" variant="outlineLight" size="lg">
              Scopri i 99 Castelli
            </Button>
          </div>
        </div>
      </Section>
    </PageLayout>
  );
};

export default LAquilaCecilia;
